import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FlameIcon, StarIcon, MapPinIcon, XIcon } from 'lucide-react';
import { cantones, restaurants } from '../data/mockData';
interface HeroSectionProps {
  onSelectCanton: (id: string) => void;
  onSelectRestaurant: (id: string) => void;
}
export function HeroSection({
  onSelectCanton,
  onSelectRestaurant
}: HeroSectionProps) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const normalizeText = (text: string) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const results = query.trim().length >= 2 ? restaurants.filter(r => {
    const q = normalizeText(query);
    return normalizeText(r.name).includes(q) || normalizeText(r.description).includes(q) || r.tags.some(t => normalizeText(t).includes(q)) || normalizeText(r.categoryId).includes(q) || r.menuItems.some(m => normalizeText(m.name).includes(q) || normalizeText(m.description).includes(q));
  }) : [];
  const showDropdown = isFocused && query.trim().length >= 2;
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = (id: string) => {
    setQuery('');
    setIsFocused(false);
    onSelectRestaurant(id);
  };
  return <section className="relative overflow-hidden bg-bg-primary" data-id="element-93">
      {/* Facebook-style cover photo */}
      <div className="relative w-full" data-id="element-94">
        <div className="w-full aspect-[16/5] md:aspect-[16/4.5] lg:aspect-[16/4] overflow-hidden" data-id="element-95">
          <img src="https://cdn.magicpatterns.com/uploads/nBr49UMZDSNu1KP1Y6Z27Q/hf_20260312_220931_3924f6e0-901a-4af9-8995-538fccad7b51.jpg" alt="Distrito LS - Street Food Empire" className="w-full h-full object-cover object-center" data-id="element-96" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bg-primary to-transparent" data-id="element-97" />
      </div>

      {/* Content below the cover */}
      <div className="relative z-10 max-w-4xl mx-auto text-center px-4 -mt-8 pb-20" data-id="element-98">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-99">
          <p className="text-xl md:text-2xl font-body font-medium text-brand-gold mb-2" data-id="element-100">
            Donde Los Santos viene a comer
          </p>
          <p className="max-w-2xl mx-auto text-lg font-body text-text-secondary mb-12 leading-relaxed" data-id="element-101">
            Descubrí los mejores restaurantes, sodas y cafeterías de la Zona de
            Los Santos. Todo en un solo lugar.
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="max-w-2xl mx-auto mb-10" ref={wrapperRef} data-id="element-102">
          <div className="relative" data-id="element-103">
            <div className="relative rounded-full bg-bg-secondary border border-border p-2 flex items-center shadow-card" data-id="element-104">
              <div className="pl-4 text-text-muted" data-id="element-105">
                <SearchIcon className="w-6 h-6" data-id="element-106" />
              </div>
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} onFocus={() => setIsFocused(true)} placeholder="¿Qué tenés ganas de comer hoy?" className="w-full py-3 px-4 outline-none font-body text-text-primary text-lg bg-transparent" data-id="element-107" />
              {query && <button onClick={() => setQuery('')} className="p-2 text-text-muted hover:text-white transition-colors" data-id="element-108">
                  <XIcon className="w-5 h-5" data-id="element-109" />
                </button>}
              <button className="bg-gradient-fire text-white font-body font-bold px-8 py-3 rounded-full hover:shadow-fire transition-all flex items-center gap-2 flex-shrink-0" data-id="element-110">
                <FlameIcon className="w-5 h-5" data-id="element-111" />
                Buscar
              </button>
            </div>

            {/* Search Results Dropdown */}
            <AnimatePresence data-id="element-112">
              {showDropdown && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.2
            }} className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border rounded-2xl shadow-2xl overflow-hidden z-50" data-id="element-113">
                  {results.length > 0 ? <div className="max-h-80 overflow-y-auto" data-id="element-114">
                      {results.slice(0, 6).map(r => {
                  const canton = cantones.find(c => c.id === r.cantonId);
                  return <button key={r.id} onClick={() => handleSelect(r.id)} className="w-full flex items-center gap-4 p-4 hover:bg-bg-secondary transition-colors text-left border-b border-border last:border-b-0" data-id="element-115">
                            <img src={r.image} alt={r.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" data-id="element-116" />
                            <div className="flex-1 min-w-0" data-id="element-117">
                              <p className="font-body font-bold text-white truncate" data-id="element-118">
                                {r.name}
                              </p>
                              <div className="flex items-center gap-2 mt-1" data-id="element-119">
                                <MapPinIcon className="w-3 h-3 text-brand-orange flex-shrink-0" data-id="element-120" />
                                <span className="font-body text-xs text-text-muted truncate" data-id="element-121">
                                  {canton?.name}
                                </span>
                                <span className="text-text-muted" data-id="element-122">·</span>
                                <span className="font-body text-xs text-brand-gold" data-id="element-123">
                                  {r.priceRange}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0" data-id="element-124">
                              <StarIcon className="w-3.5 h-3.5 text-brand-gold fill-brand-gold" data-id="element-125" />
                              <span className="font-body text-sm font-bold text-white" data-id="element-126">
                                {r.rating}
                              </span>
                            </div>
                          </button>;
                })}
                      {results.length > 6 && <div className="p-3 text-center" data-id="element-127">
                          <span className="font-body text-sm text-text-muted" data-id="element-128">
                            +{results.length - 6} resultados más
                          </span>
                        </div>}
                    </div> : <div className="p-8 text-center" data-id="element-129">
                      <p className="font-body text-text-muted" data-id="element-130">
                        No encontramos resultados para "
                        <span className="text-white" data-id="element-131">{query}</span>"
                      </p>
                      <p className="font-body text-sm text-text-muted mt-1" data-id="element-132">
                        Probá con: hamburguesa, café, trucha, casado...
                      </p>
                    </div>}
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Canton Quick Links */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mb-12" data-id="element-133">
          <p className="font-body text-sm text-text-muted mb-4 uppercase tracking-wider font-semibold" data-id="element-134">
            Explorá la zona
          </p>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-135">
            {cantones.map(canton => <button key={canton.id} onClick={() => onSelectCanton(canton.id)} className="bg-bg-secondary border border-border text-text-secondary hover:text-brand-gold hover:border-brand-gold/40 px-6 py-2 rounded-full font-body text-sm transition-all" data-id="element-136">
                {canton.name}
              </button>)}
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="flex flex-wrap justify-center gap-8 md:gap-16 border-t border-border pt-8" data-id="element-137">
          <div className="text-center" data-id="element-138">
            <p className="font-heading text-4xl text-brand-gold" data-id="element-139">19</p>
            <p className="font-body text-sm text-white" data-id="element-140">Restaurantes</p>
          </div>
          <div className="text-center" data-id="element-141">
            <p className="font-heading text-4xl text-brand-gold" data-id="element-142">3</p>
            <p className="font-body text-sm text-white" data-id="element-143">Cantones</p>
          </div>
          <div className="text-center" data-id="element-144">
            <p className="font-heading text-4xl text-brand-gold" data-id="element-145">5</p>
            <p className="font-body text-sm text-white" data-id="element-146">Categorías</p>
          </div>
        </motion.div>
      </div>
    </section>;
}